import React, { useState, useEffect, useRef, useCallback } from 'react';
import MessageList from './components/MessageList';
import ChatInput from './components/ChatInput';
import { useChatApi } from './hooks/useChatApi';

const App = () => {
  const [messages, setMessages] = useState([{ 
    sender: "bot", 
    text: "Hello! Ask me anything." 
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const messagesEndRef = useRef(null);
  
  const { uploadFile, sendMessage } = useChatApi();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleFileUpload = useCallback(async (file) => {
    if (!file) return;
    setUploadingCount(prev => prev + 1);
    
    try {
      const result = await uploadFile(file);
      if (result) {
        setUploadedFiles(prev => [...prev, result]);
      }
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploadingCount(prev => prev - 1);
    }
  }, [uploadFile]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    files.forEach(file => {
      handleFileUpload(file);
    });
  }, [handleFileUpload]);

  const handleFileDelete = useCallback((fileUuid) => {
    setUploadedFiles(prev => prev.filter(file => file.file_uuid !== fileUuid));
  }, []);

  const handleSend = useCallback((text, files) => {
    if (isStreaming) return;
    if (uploadingCount > 0) return;
    setIsStreaming(true);
    const fileUuids = files.map(file => file.file_uuid);
    
    const userMessage = {
      sender: "user",
      text: text,
      files: files
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    sendMessage(
      text, 
      fileUuids,
      setMessages, 
      setLoading,
      () => setIsStreaming(false)
    );
    setUploadedFiles([]);
  }, [isStreaming, uploadingCount, sendMessage]);

  return (
    <div 
      className="relative flex flex-col h-screen bg-neutral-800 text-gray-100"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="flex-grow overflow-y-auto flex justify-center p-4">
        <div className="w-full max-w-[720px] px-4">
          <MessageList messages={messages} loading={loading} />
          <div ref={messagesEndRef} />
        </div>
      </div>

      <ChatInput
        input={input}
        setInput={setInput}
        onSend={handleSend}
        loading={loading}
        onFileUpload={handleFileUpload}
        onFileDelete={handleFileDelete}
        uploadedFiles={uploadedFiles}
        uploadingCount={uploadingCount}
        isStreaming={isStreaming}
      />

      {isDragging && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center flex-col items-center z-50">
          <img
            src="/drag-upload.svg"
            alt="Upload Icon"
            width="120"
            height="120"
            className="block mb-3"
          />
          <h3 className="text-white text-[22px] mb-1">Add anything</h3>
          <p className="text-white text-base">Drop files to add them to the conversation.</p>
        </div>
      )}
    </div>
  );
};

export default App;